import "../ReservationForm/BookingForm.css";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import SnackBar from "../../components/SnackBar/SnackBar";
import {
  getCurrentUser,
  getTaxList,
  insertRoomType,
  editRoomType,
} from "../../api-config/config";

const menuItems = ["Light", "AC", "TV", "Fan"];

const NewRoomType = (props: any) => {
  const {
    handleClose,
    onRoomTypeAdded,
    roomTypeData,
    onSuccess,
    setLoading,
    showSnackbar
  } = props;
  const loggedUser = getCurrentUser();
  const [selectedAmenities, setAmenities] = useState<string[]>([]);
  const [selectedTaxList, setSelectedTaxList] = useState<string[]>([]);
  const [taxList, setTaxList] = useState<any[]>([]);
  const [showAmenitiesDropdown, setShowAmenitiesDropdown] = useState(false);
  const [showTaxDropdown, setShowTaxDropdown] = useState(false);

  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: "",
    type: "error" as "error" | "success",
  });

  const amenitiesRef = useRef<HTMLDivElement>(null);
  const taxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getTaxList(loggedUser?.hotel_code).then((res: any) => {
      if (res?.status_message === "success") {
        setTaxList(res.data || []);
      }
    });
    console.log("roomTypeData", roomTypeData);
  }, []);

  useEffect(() => {
    if (!roomTypeData) {
      setAmenities([]);
      setSelectedTaxList([]);
      setShowAmenitiesDropdown(false);
      setShowTaxDropdown(false);
      return;
    }

    setAmenities(roomTypeData?.amenities_ids?.split(",") || []);

    if (roomTypeData?.tax_ids && taxList.length > 0) {
      const ids: number[] = roomTypeData.tax_ids
        .split(",")
        .map((id: string) => Number(id.trim()));
      const matchedTaxes = taxList.filter((t) => ids.includes(Number(t.id)));
      const matchedNames: string[] = matchedTaxes.map((t) => t.name);

      const missingIds: number[] = ids.filter(
        (id: number) => !taxList.some((t) => Number(t.id) === id),
      );

      const finalNames: string[] = [
        ...matchedNames,
        ...(missingIds.length > 0 ? ["Deleted Tax"] : []),
      ];

      setSelectedTaxList(finalNames);
    } else {
      setSelectedTaxList([]);
    }
  }, [roomTypeData, taxList]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        amenitiesRef.current &&
        !amenitiesRef.current.contains(e.target as Node)
      )
        setShowAmenitiesDropdown(false);
      if (taxRef.current && !taxRef.current.contains(e.target as Node))
        setShowTaxDropdown(false);
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleAmenity = (name: string) => {
    setAmenities((prev) =>
      prev.includes(name) ? prev.filter((selectedAmenity) => selectedAmenity !== name) : [...prev, name],
    );
  };

  const toggleTax = (taxName: string) => {
    setSelectedTaxList((previousTaxes) => {
      let updatedTaxes: string[];

      if (previousTaxes.includes(taxName)) {
        updatedTaxes = previousTaxes.filter(
          (selectedTax) => selectedTax !== taxName
        );
      } else {
        updatedTaxes = [
          ...previousTaxes.filter(
            (selectedTax) => selectedTax !== "Deleted Tax"
          ),
          taxName,
        ];
      }

      return updatedTaxes;
    });
  };

  const extractTaxIds = () => {
    return selectedTaxList
      .map((name) => taxList.find((tax) => tax.name === name)?.id)
      .filter(Boolean);
  };

  const defaultValues = {
    room_type: roomTypeData?.roomtype || "",
    baseOccupancy: roomTypeData?.base_occupancy || "",
    higherOccupancy: roomTypeData?.occupancy || "",
    noOfRoom: roomTypeData?.stock || "",
    extraBed: roomTypeData?.extra || "",
    basePrice: roomTypeData?.price || "",
    extraBedPrice: roomTypeData?.extra_bed_price || "",
    service_charge: roomTypeData?.service_charge || "",
  };

  const validation = Yup.object({
    room_type: Yup.string().required("Room Type is required"),
    baseOccupancy: Yup.string().required("Base Occupancy is required"),
    higherOccupancy: Yup.string().required("Higher Occupancy is required"),
    extraBed: Yup.string().required("Extra Bed is required"),
    noOfRoom: Yup.string().required("No Of Rooms is required"),
    basePrice: Yup.number().required("Base Price is required"),
    extraBedPrice: Yup.number()
    .typeError("Extra Bed Price must be a number")
    .required("Extra Bed Price is required")
    .min(0, "Extra Bed Price cannot be negative"),
  });

  const handleSubmit = (values: any) => {
    if (selectedTaxList.length === 0) {
      setSnackbar({
        visible: true,
        message: "Please select at least one Tax.",
        type: "error",
      });
      return;
    }

    if (selectedTaxList.includes("Deleted Tax")) {
      setSnackbar({
        visible: true,
        message: "Some taxes were deleted. Please reselect taxes.",
        type: "error",
      });
      return;
    }

    const payload = {
      room_type_name: values.room_type,
      base_occupancy: Number(values.baseOccupancy),
      higher_occupancy: Number(values.higherOccupancy),
      extra_bed: Number(values.extraBed),
      no_rooms_stock: Number(values.noOfRoom),
      price: Number(values.basePrice),
      extra_bed_price: Number(values.extraBedPrice),
      created_by: 0,
      hotel_code: loggedUser?.hotel_code,
      amenities_ids: selectedAmenities,
      tax_ids: extractTaxIds(),
      service_charge: values.service_charge ? Number(values.service_charge) : 0,
    };

    const api = roomTypeData?.room_type_id
      ? editRoomType({ id: roomTypeData?.room_type_id, ...payload })
      : insertRoomType(
          payload.room_type_name,
          payload.base_occupancy,
          payload.higher_occupancy,
          payload.extra_bed,
          payload.no_rooms_stock,
          payload.price,
          payload.extra_bed_price,
          payload.created_by,
          payload.hotel_code,
          payload.amenities_ids,
          payload.tax_ids,
          payload.service_charge,
        );

    setLoading(true);

    api.then((res: any) => {
      if (res?.status_message === "failed") {
        setSnackbar({
          visible: true,
          message: res.message || "Something went wrong",
          type: "error",
        });
        setLoading(false);
      } else {
        const message = roomTypeData?.room_type_id
          ? "Room Type Updated Successfully"
          : "Room Type Added Successfully";
        
        setLoading(false);
        
        if (showSnackbar) {
          showSnackbar(message, "success", () => {
            handleClose();
            setTimeout(() => {
              onRoomTypeAdded();
            }, 3000);
          });
        } else {
          handleClose();
          onRoomTypeAdded();
        }
      }
    })
    .catch(() => {
      setSnackbar({
        visible: true,
        message: "Something went wrong",
        type: "error",
      });
      setLoading(false);
    });
  };

  return (
    <div className="booking-form-wrapper">
      <Formik
        key={roomTypeData?.room_type_id || "new"}
        enableReinitialize
        initialValues={defaultValues}
        validationSchema={validation}
        onSubmit={(values, { validateForm, setTouched }) => {
          validateForm().then((errors) => {
            if (Object.keys(errors).length > 0) {
              setTouched(
                Object.keys(errors).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {} as any),
              );

              setSnackbar({
                visible: true,
                message: Object.values(errors).join(", "),
                type: "error",
              });
              return;
            }
            handleSubmit(values);
          });
        }}
      >
        <Form className="grid lg:grid-cols-1 p-8">
          <div className="form-group">
            <label className="pb-2">
              Room Type <span className="text-red-600">*</span>
            </label>
            <Field
              name="room_type"
              className="con-field"
              placeholder="Enter room type"
            />
            <ErrorMessage
              name="room_type"
              component="div"
              className="text-red-600 text-sm mt-1"
            />
          </div>

          {/* Occupancy */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="form-group">
              <label className="py-2">
                Base Occupancy <span className="text-red-600">*</span>
              </label>
              <Field
                as="select"
                name="baseOccupancy"
                placeholder="Select base occupancy"
              >
                <option value="">Select</option>
                {[...Array(20)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="baseOccupancy"
                component="div"
                className="text-red-600 text-sm mt-1"
              />
            </div>
            <div className="form-group">
              <label className="py-2">
                Higher Occupancy <span className="text-red-600">*</span>
              </label>
              <Field
                as="select"
                name="higherOccupancy"
                placeholder="Select higher occupancy"
              >
                <option value="">Select</option>
                {[...Array(20)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="higherOccupancy"
                component="div"
                className="text-red-600 text-sm mt-1"
              />
            </div>
          </div>

          {/* Extra Bed + Rooms */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="form-group">
              <label className="py-2">
                Extra Bed <span className="text-red-600">*</span>
              </label>
              <Field as="select" name="extraBed" placeholder="Select extra bed">
                <option value="">Select</option>
                {[...Array(15)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="extraBed"
                component="div"
                className="text-red-600 text-sm mt-1"
              />
            </div>
            <div className="form-group">
              <label className="py-2">
                No Of Rooms <span className="text-red-600">*</span>
              </label>
              <Field
                name="noOfRoom"
                className="con-field"
                type="number"
                placeholder="Enter number of rooms"
              />
              <ErrorMessage
                name="noOfRoom"
                component="div"
                className="text-red-600 text-sm mt-1"
              />
            </div>
          </div>

          {/* Dropdowns */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div ref={taxRef} className="form-group relative">
              <label className="py-2">
                Select Tax <span className="text-red-600">*</span>
              </label>
              <div
                className="con-field"
                onClick={() => setShowTaxDropdown(!showTaxDropdown)}
                style={{
                  color: selectedTaxList.includes("Deleted Tax")
                    ? "#dc2626"
                    : selectedTaxList.length === 0
                    ? "#9ca3af"
                    : "#000",
                  fontWeight: selectedTaxList.includes("Deleted Tax")
                    ? 600
                    : 400,
                }}
              >
                {selectedTaxList.length === 0
                  ? "Select Tax"
                  : selectedTaxList.join(", ")}
              </div>
              {showTaxDropdown && (
                <div
                  className="border absolute w-full"
                  style={{
                    background: "#fff",
                    zIndex: 10,
                    paddingLeft: "12px",
                    lineHeight: "28px",
                  }}
                >
                  {taxList.map((t) => (
                    <div key={t.id} onClick={() => toggleTax(t.name)}>
                      <input
                        type="checkbox"
                        checked={selectedTaxList.includes(t.name)}
                        readOnly
                      />{" "}
                      {t.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div ref={amenitiesRef} className="form-group relative">
              <label className="py-2">Amenities</label>
              <div
                className="con-field"
                onClick={() => setShowAmenitiesDropdown(!showAmenitiesDropdown)}
                style={{
                  color: selectedAmenities.length === 0 ? "#9ca3af" : "#000",
                }}
              >
                {selectedAmenities.join(", ") || "Select Amenities"}
              </div>
              {showAmenitiesDropdown && (
                <div
                  className="border absolute w-full"
                  style={{
                    background: "#fff",
                    zIndex: 10,
                    paddingLeft: "12px",
                    lineHeight: "28px",
                  }}
                >
                  {menuItems.map((a) => (
                    <div key={a} onClick={() => toggleAmenity(a)}>
                      <input
                        type="checkbox"
                        checked={selectedAmenities.includes(a)}
                        readOnly
                      />{" "}
                      {a}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Prices */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="form-group">
              <label className="py-2">
                Base Price <span className="text-red-600">*</span>
              </label>
              <Field
                name="basePrice"
                className="con-field"
                type="number"
                placeholder="Enter base price"
              />
              <ErrorMessage
                name="basePrice"
                component="div"
                className="text-red-600 text-sm mt-1"
              />
            </div>
            <div className="form-group">
              <label className="py-2">Extra Bed Price <span className="text-red-600">*</span></label>
              <Field
                name="extraBedPrice"
                className="con-field"
                type="number"
                placeholder="Enter extra bed price"
              />
              <ErrorMessage
                name="extraBedPrice"
                component="div"
                className="text-red-600 text-sm mt-1"
              />
            </div>
          </div>

          {/* Service Charge */}
          <div className="form-group">
            <label className="py-2">Service Charge (%)</label>
            <Field
              name="service_charge"
              className="con-field"
              type="number"
              placeholder="Enter service charge percentage"
            />
            <ErrorMessage
              name="service_charge"
              component="div"
              className="text-red-600 text-sm mt-1"
            />
          </div>

          {/* Buttons */}
          <div className="form-actions pt-4">
            <button type="button" onClick={handleClose} className="bg-sky-300">
              Cancel
            </button>
            <button type="submit" className="bg-sky-300">
              Save
            </button>
          </div>
        </Form>
      </Formik>

      <SnackBar
        visible={snackbar.visible}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((s) => ({ ...s, visible: false }))}
      />
    </div>
  );
};

export default NewRoomType;